import catalog from '@/config/messages.json'

type MessageCatalog = typeof catalog

/**
 * Resolves a dot-notation path against the message catalog.
 * Returns the string value, or undefined if the path is missing or
 * resolves to a non-string (i.e. a nested object).
 *
 * Example:
 *   resolvePath('photo.error.remove_failed')
 *   → "Failed to remove the photo. Please try again."
 */
function resolvePath(path: string): string | undefined {
  const parts = path.split('.')
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let node: any = catalog
  for (const part of parts) {
    if (node == null || typeof node !== 'object') return undefined
    node = node[part]
  }
  return typeof node === 'string' ? node : undefined
}

/** Same traversal as `resolvePath`, but for array-valued entries (e.g. bulleted tour step
 * descriptions) instead of strings. */
function resolveListPath(path: string): string[] | undefined {
  const parts = path.split('.')
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let node: any = catalog
  for (const part of parts) {
    if (node == null || typeof node !== 'object') return undefined
    node = node[part]
  }
  return Array.isArray(node) ? (node as string[]) : undefined
}

/**
 * Interpolates `{key}` placeholders in a message template.
 *
 * Example:
 *   interpolate('OCR scan failed for "{filename}"', { filename: 'photo.jpg' })
 *   → 'OCR scan failed for "photo.jpg"'
 */
function interpolate(template: string, params: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (_, key) => params[key] ?? `{${key}}`)
}

/**
 * Retrieve a user-facing message by dot-notation path with optional
 * template variable substitution and graceful fallback.
 *
 * @param path   - Dot-notation key, e.g. `'photo.error.remove_failed'`
 * @param params - Optional `{ key: value }` pairs for `{key}` placeholders
 * @returns      - The resolved (and interpolated) message string.
 *                 Falls back to a formatted version of the path when the
 *                 key is not found, so the UI never shows raw undefined.
 *
 * @example
 *   getMessage('valuation.success.draft_saved')
 *   getMessage('photo.error.process_failed', { filename: 'img.jpg' })
 */
export function getMessage(
  path: keyof MessagePaths,
  params?: Record<string, string>
): string {
  const raw = resolvePath(path as string)
  if (!raw) {
    // Fallback: turn 'module.type.key' → 'Module type key'
    const fallback = (path as string)
      .split('.')
      .pop()
      ?.replace(/_/g, ' ') ?? path as string
    return fallback.charAt(0).toUpperCase() + fallback.slice(1)
  }
  return params ? interpolate(raw, params) : raw
}

/**
 * Retrieve a list-valued user-facing message (e.g. bulleted tour step descriptions) by
 * dot-notation path. Falls back to an empty array when the path is missing.
 *
 * @example
 *   getMessageList('tour.steps.claims_list.description')
 */
export function getMessageList(path: keyof MessageListPaths): string[] {
  return resolveListPath(path as string) ?? []
}

// ── Strongly-typed path union ──────────────────────────────────────────────
// Enumerates every leaf path in the catalog so `getMessage` arguments are
// checked at compile-time.  Add new entries to messages.json and this type
// automatically expands on the next TS check.

type Leaves<T, Prefix extends string = ''> = {
  [K in keyof T]: T[K] extends readonly string[]
    ? never
    : T[K] extends string
      ? Prefix extends '' ? `${string & K}` : `${Prefix}.${string & K}`
      : T[K] extends object
        ? Leaves<T[K], Prefix extends '' ? `${string & K}` : `${Prefix}.${string & K}`>
        : never
}[keyof T]

// Same traversal, but collects paths whose *value* is a string array instead of a string —
// used for `getMessageList` (e.g. bulleted tour step descriptions).
type ListLeaves<T, Prefix extends string = ''> = {
  [K in keyof T]: T[K] extends readonly string[]
    ? Prefix extends '' ? `${string & K}` : `${Prefix}.${string & K}`
    : T[K] extends object
      ? ListLeaves<T[K], Prefix extends '' ? `${string & K}` : `${Prefix}.${string & K}`>
      : never
}[keyof T]

export type MessagePaths = { [P in Leaves<Omit<MessageCatalog, '_comment'>>]: string }
export type MessageListPaths = { [P in ListLeaves<Omit<MessageCatalog, '_comment'>>]: string[] }
